import type { IEventBus } from "@/domains/events"
import type { OrchestratedQueryStore } from "@/domains/library"
import { OrchestratedQuery } from "@/domains/library/orchestrated-query"

import type { ISwapCoordinator } from "./interfaces"
import {
  SwapApprovalCompletedEvent,
  SwapApprovalStartedEvent,
  SwapCompletedEvent,
  SwapErrorEvent,
  SwapExecutionStartedEvent,
  SwapQuotesPausedEvent,
  SwapQuotesResumedEvent,
} from "./events"

/**
 * Swap Coordinator
 * Coordinates pausing/resuming all aggregator quote queries during swap execution
 * Listens to swap lifecycle events and automatically manages query state
 *
 * Important: On successful swap completion, quotes remain paused until user enters
 * a new input amount. This prevents unnecessary API calls for zero amounts.
 */
export class SwapCoordinator implements ISwapCoordinator {
  private quoteQueryNames: Set<string> = new Set()
  private isPausedForSwap: boolean = false

  constructor(
    private readonly orchestratedQueryStore: OrchestratedQueryStore,
    private readonly eventBus: IEventBus,
  ) {
    // Subscribe to swap lifecycle events
    this.eventBus.subscribe(SwapApprovalStartedEvent.TYPE, this.handleApprovalStarted.bind(this))
    this.eventBus.subscribe(SwapApprovalCompletedEvent.TYPE, this.handleApprovalCompleted.bind(this))
    this.eventBus.subscribe(SwapExecutionStartedEvent.TYPE, this.handleExecutionStarted.bind(this))
    this.eventBus.subscribe(SwapCompletedEvent.TYPE, this.handleSwapCompleted.bind(this))
    this.eventBus.subscribe(SwapErrorEvent.TYPE, this.handleSwapError.bind(this))
  }

  /**
   * Register a quote query name for coordination
   * Called by aggregators during initialization
   */
  registerQuoteQuery(queryName: string): void {
    this.quoteQueryNames.add(queryName)
  }

  /**
   * Get all registered quote queries
   */
  getQuoteQueries(): Array<{ getName: string; stop(): void; start(): void }> {
    return Array.from(this.quoteQueryNames)
      .map((name) => this.orchestratedQueryStore.getQueryByName(name))
      .filter((q): q is OrchestratedQuery<unknown> => q !== undefined)
      .map((query) => ({
        getName: query.getName,
        stop: () => query.stop(),
        start: () => query.start(),
      }))
  }

  /**
   * Pause all quote queries
   */
  pauseAllQuoteQueries(reason: "approval" | "execution"): void {
    const queries = this.getQuoteQueries()
    queries.forEach((query) => query.stop())
    this.eventBus.publish(new SwapQuotesPausedEvent(reason))
    // Mark as paused for swap if it's execution (not approval)
    if (reason === "execution") {
      this.isPausedForSwap = true
    }
  }

  /**
   * Resume all quote queries
   */
  resumeAllQuoteQueries(): void {
    const queries = this.getQuoteQueries()
    queries.forEach((query) => query.start())
    this.eventBus.publish(new SwapQuotesResumedEvent())
    this.isPausedForSwap = false
  }

  /**
   * Handle approval started event
   */
  private handleApprovalStarted(_event: SwapApprovalStartedEvent): void {
    this.pauseAllQuoteQueries("approval")
    // Don't set isPausedForSwap - we'll resume after approval if needed
  }

  /**
   * Handle approval completed event
   * Quotes remain paused - swap execution will start next
   */
  private handleApprovalCompleted(_event: SwapApprovalCompletedEvent): void {
    // Don't resume quotes - swap execution will start next and quotes are already paused
    // The execution started event will handle pausing (if not already paused)
  }

  /**
   * Handle execution started event
   */
  private handleExecutionStarted(_event: SwapExecutionStartedEvent): void {
    this.pauseAllQuoteQueries("execution")
    // isPausedForSwap is set in pauseAllQuoteQueries
  }

  /**
   * Handle swap completed event
   *
   * Important: We do NOT resume quotes here. Quotes remain paused until
   * the user enters a new input amount. The SwapQuoteRequestedEvent will
   * handle resuming when a new quote is requested.
   */
  private handleSwapCompleted(_event: SwapCompletedEvent): void {
    // Don't resume quotes - wait for user to enter new amount
    // The SwapQuoteRequestedEvent will handle resuming
    this.isPausedForSwap = true
  }

  /**
   * Handle swap error event
   * Resume quotes immediately so user can retry
   */
  private handleSwapError(_event: SwapErrorEvent): void {
    this.resumeAllQuoteQueries()
    this.isPausedForSwap = false
  }
}
